import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-101 Comuna Despegue (GDD §10.3), the tutorial and the mission of the vertical slice, written by hand
// with the format of §15.6. 600 u at 5.0 u/s: takeoff, warm-up (22 %), tension (30 %), CP-101-A at x 300,
// respite (10 %), peak (38 %) and the airstrip. Columns every 9 u with the 4.0 u gap of world 1 (§5.4);
// dollar bundles in arcs of three in the open air between columns, over the safe route (§7); only
// gallinazos (EN-01, #71), none in the warm-up: placed 3 u ahead of the open air between two columns, which is
// where they meet the plane after flying 1 u/s left, 2.3 u above or below both gaps. All x are local.

const column = (x: number, gapY: number, skin: 'w1-power-tower' | 'w1-rock-houses'): ColumnDef => ({ x, gapY, gapH: 4.0, skin });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const gallinazo = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-01' });

const takeoff: SegmentDef = { id: 'w1-comuna-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w1-comuna-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 55,
  columns: [
    column(20.0, 6.0, 'w1-power-tower'),
    column(29.0, 6.5, 'w1-rock-houses'),
    column(38.0, 6.0, 'w1-power-tower'),
    column(47.0, 5.5, 'w1-rock-houses'),
  ],
  enemies: [],
  pickups: [
    dollar(23.5, 6.25),
    dollar(24.5, 6.55),
    dollar(25.5, 6.25),
    dollar(50.5, 5.75),
    dollar(51.5, 6.05),
    dollar(52.5, 5.75),
  ],
};

const warmup02: SegmentDef = {
  id: 'w1-comuna-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 55,
  columns: [
    column(1.0, 6.0, 'w1-power-tower'),
    column(10.0, 6.5, 'w1-rock-houses'),
    column(19.0, 7.0, 'w1-power-tower'),
    column(28.0, 6.5, 'w1-rock-houses'),
    column(37.0, 6.0, 'w1-power-tower'),
    column(46.0, 6.5, 'w1-rock-houses'),
  ],
  enemies: [],
  pickups: [
    dollar(22.5, 6.75),
    dollar(23.5, 7.05),
    dollar(24.5, 6.75),
  ],
};

const tension01: SegmentDef = {
  id: 'w1-comuna-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(0.0, 7.5, 'w1-power-tower'),
    column(9.0, 6.0, 'w1-rock-houses'),
    column(18.0, 7.5, 'w1-power-tower'),
    column(27.0, 5.5, 'w1-rock-houses'),
    column(36.0, 7.0, 'w1-power-tower'),
    column(45.0, 8.5, 'w1-rock-houses'),
  ],
  enemies: [],
  pickups: [
    dollar(3.5, 6.75),
    dollar(4.5, 7.05),
    dollar(5.5, 6.75),
    dollar(30.5, 6.25),
    dollar(31.5, 6.55),
    dollar(32.5, 6.25),
  ],
};

const tension02: SegmentDef = {
  id: 'w1-comuna-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(4.0, 7.0, 'w1-power-tower'),
    column(13.0, 5.5, 'w1-rock-houses'),
    column(22.0, 7.0, 'w1-power-tower'),
    column(31.0, 8.0, 'w1-rock-houses'),
    column(40.0, 6.5, 'w1-power-tower'),
    column(49.0, 5.0, 'w1-rock-houses'),
  ],
  enemies: [
    gallinazo(29.5, 10.3),
  ],
  pickups: [
    dollar(7.5, 6.25),
    dollar(8.5, 6.55),
    dollar(9.5, 6.25),
  ],
};

const tension03: SegmentDef = {
  id: 'w1-comuna-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(8.0, 6.5, 'w1-power-tower'),
    column(17.0, 8.0, 'w1-rock-houses'),
    column(26.0, 6.5, 'w1-power-tower'),
    column(35.0, 5.5, 'w1-rock-houses'),
    column(44.0, 6.0, 'w1-power-tower'),
  ],
  enemies: [
    gallinazo(33.5, 3.2),
  ],
  pickups: [
    dollar(11.5, 7.25),
    dollar(12.5, 7.55),
    dollar(13.5, 7.25),
  ],
};

const respite01: SegmentDef = {
  id: 'w1-comuna-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 50,
  columns: [
    column(3.0, 6.5, 'w1-power-tower'),
    column(12.0, 6.0, 'w1-rock-houses'),
    column(21.0, 6.5, 'w1-power-tower'),
    column(30.0, 6.0, 'w1-rock-houses'),
    column(39.0, 6.5, 'w1-power-tower'),
    column(48.0, 6.0, 'w1-rock-houses'),
  ],
  enemies: [],
  pickups: [
    dollar(6.5, 6.25),
    dollar(7.5, 6.55),
    dollar(8.5, 6.25),
    dollar(33.5, 6.25),
    dollar(34.5, 6.55),
    dollar(35.5, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w1-comuna-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    column(7.0, 8.5, 'w1-power-tower'),
    column(16.0, 6.0, 'w1-rock-houses'),
    column(25.0, 9.0, 'w1-power-tower'),
    column(34.0, 6.5, 'w1-rock-houses'),
    column(43.0, 4.0, 'w1-power-tower'),
    column(52.0, 7.0, 'w1-rock-houses'),
    column(61.0, 9.5, 'w1-power-tower'),
  ],
  enemies: [
    gallinazo(41.5, 8.8),
  ],
  pickups: [
    dollar(19.5, 7.5),
    dollar(20.5, 7.8),
    dollar(21.5, 7.5),
  ],
};

const peak02: SegmentDef = {
  id: 'w1-comuna-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    column(5.0, 7.0, 'w1-power-tower'),
    column(14.0, 4.5, 'w1-rock-houses'),
    column(23.0, 7.5, 'w1-power-tower'),
    column(32.0, 5.0, 'w1-rock-houses'),
    column(41.0, 8.0, 'w1-power-tower'),
    column(50.0, 5.5, 'w1-rock-houses'),
    column(59.0, 8.5, 'w1-power-tower'),
  ],
  enemies: [
    gallinazo(30.5, 2.7),
  ],
  pickups: [
    dollar(8.5, 5.75),
    dollar(9.5, 6.05),
    dollar(10.5, 5.75),
  ],
};

const peak03: SegmentDef = {
  id: 'w1-comuna-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [
    column(3.0, 6.0, 'w1-power-tower'),
    column(12.0, 3.5, 'w1-rock-houses'),
    column(21.0, 6.5, 'w1-power-tower'),
    column(30.0, 9.0, 'w1-rock-houses'),
    column(39.0, 6.5, 'w1-power-tower'),
    column(48.0, 6.0, 'w1-rock-houses'),
  ],
  enemies: [
    gallinazo(19.5, 8.8),
  ],
  pickups: [
    dollar(6.5, 4.75),
    dollar(7.5, 5.05),
    dollar(8.5, 4.75),
    dollar(33.5, 7.75),
    dollar(34.5, 8.05),
    dollar(35.5, 7.75),
  ],
};

const airstrip: SegmentDef = { id: 'w1-comuna-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_101_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_101: LevelDef = {
  id: '1-1',
  op: 'OP-101',
  name: 'Comuna Despegue',
  world: 1,
  scrollSpeed: 5.0,
  segments: OP_101_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-101-A', x: 300 }],
  ending: 'airstrip',
};
